import React from 'react';

import { Box, Grid, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { useAttestationsListQuery } from 'src/api';
import { Button, FullscreenLoader } from 'src/components/common';
import { CheckboxField, TextField } from 'src/components/finalForm';
import { AttestationPurpose } from 'src/constants/attestation';
import { useAuthSignup } from 'src/hooks/auth';
import { shouldDisplayError } from 'src/utils/shouldDisplayError';

import { RenderErrorMessage } from '../../RenderErrorMessage';
import { EmployeeVerificationPreferenceFormValues } from '../types';
import { employeeAttestationsFormValidationSchema } from '../utils';

export const EmployeeAttestationsForm: React.FC = () => {
  const { goToNextStep, goToPreviousStep } = useAuthSignup();

  const { data, loading } = useAttestationsListQuery();

  const attestations = data?.attestationList.filter(attestation =>
    attestation.purpose?.includes(AttestationPurpose.forEmployers),
  );

  const renderFormContent = React.useCallback(() => {
    if (loading) {
      return <FullscreenLoader />;
    }

    return (
      <Form<EmployeeVerificationPreferenceFormValues>
        onSubmit={() => goToNextStep()}
        subscription={{
          valid: true,
          values: true,
          submitting: true,
          errors: true,
          dirtyFields: true,
        }}
        containerProps={{ mt: 4 }}
        validate={validateWithYupSchema(employeeAttestationsFormValidationSchema(attestations))}
      >
        {({ submitting, valid, errors, dirtyFields }) => (
          <React.Fragment>
            <Grid
              sx={{
                pt: '15px',
                maxHeight: '459px',
                overflow: 'auto',
              }}
              size={12}
            >
              {attestations?.map(fields => (
                <React.Fragment key={fields.id}>
                  <Field
                    fullWidth
                    name={fields.name}
                    multiline
                    label={fields.name}
                    component={TextField}
                    type="text"
                    disabled
                    showErrorWhenDisabled={false}
                    sx={{
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                    slotProps={{
                      input: {
                        value: fields.text,
                        readOnly: true,
                        rows: 0,
                        sx: {
                          '&.Mui-disabled': {
                            textarea: {
                              '-webkit-text-fill-color': '#000000',
                            },
                          },
                        },
                      },
                    }}
                  />
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    mt={3}
                    mb={shouldDisplayError(fields.name, errors, dirtyFields) ? 0 : 3}
                  >
                    <Field
                      component={CheckboxField}
                      name={fields.name}
                      label={`I have read and agreed to ${fields.name}`}
                      fullWidth
                      required
                    />
                    <Button
                      variant="contained"
                      type="button"
                      component="a"
                      href={fields.pdf?.downloadUrl as string}
                      target="_blank"
                      size="small"
                      sx={{ whiteSpace: 'nowrap' }}
                      color="primary"
                    >
                      See more
                    </Button>
                  </Box>
                  {shouldDisplayError(fields.name, errors, dirtyFields) && (
                    <RenderErrorMessage errors={errors} field={fields.name} />
                  )}
                </React.Fragment>
              ))}
            </Grid>

            <Box display="flex" justifyContent="center" mt={4} gap={4}>
              <Button
                type="button"
                disabled={submitting}
                size="large"
                variant="outlined"
                color="error"
                onClick={() => goToPreviousStep()}
              >
                I DISAGREE
              </Button>
              <Button
                type="submit"
                disabled={!valid || submitting}
                loading={submitting}
                size="large"
                variant="contained"
              >
                I AGREE
              </Button>
            </Box>
          </React.Fragment>
        )}
      </Form>
    );
  }, [attestations, goToNextStep, goToPreviousStep, loading]);

  return (
    <Box>
      <Typography variant="body2" fontSize="24px">
        Attestations
      </Typography>

      <Typography variant="subtitle1" fontSize="16px" mt={1} mb={5} color="text.secondary">
        Read and agree to following to activate your account
      </Typography>
      {renderFormContent()}
    </Box>
  );
};
